import { useState } from 'react'
import MetaTags from '../components/MetaTags'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '', // Antispam honeypot field
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState(null)

  const validate = () => {
    const errs = {}
    if (!formData.name.trim()) errs.name = 'Angiv venligst dit navn'
    if (!formData.phone.trim()) {
      errs.phone = 'Angiv venligst dit telefonnummer'
    } else if (!/^[+0-9\s-]{6,16}$/.test(formData.phone.trim())) {
      errs.phone = 'Indtast venligst et gyldigt telefonnummer'
    }
    if (!formData.email.trim()) {
      errs.email = 'Angiv venligst din emailadresse'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Indtast venligst en gyldig emailadresse'
    }
    if (!formData.message.trim()) errs.message = 'Skriv venligst en kort besked'
    return errs
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setErrors({})
    setServerError(null)
    setSubmitting(true)

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          navn: formData.name,
          telefon: formData.phone,
          email: formData.email,
          beskrivelse: formData.message,
          website_url: formData.website_url, // Honeypot
        }),
      })

      if (!res.ok) {
        throw new Error('Der opstod en fejl ved afsendelse. Prøv venligst igen.')
      }

      setSubmitted(true)
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: '',
        website_url: '',
      })
    } catch (err) {
      console.error('Fejl ved kontaktformular:', err)
      setServerError(
        'Beskeden kunne ikke sendes lige nu. Du er meget velkommen til at ringe på 42 51 01 05 eller sende en mail til info@trine-rosendal.dk.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <MetaTags
        title="Kontakt - Trine Rosendal"
        description="Hypnotisør og Hypnoterapeut Trine Rosendal. Prins Burisvej 41.A, 7500 Holstebro. Tlf: 42 51 01 05. Info@trine-rosendal.dk. CVR: 31231051. Book tid online eller send en besked."
        image="https://assets.auxodev.dk/assets/0c569b3d-b522-4d44-8ece-350bff7b17df/cat-1011-scaled.jpg"
        canonicalUrl="https://trine-rosendal.dk/kontakt"
      />

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8 sm:py-14 space-y-14 sm:space-y-16">
        {/* Header Introduction */}
        <div className="max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D6E2CB] bg-[#EEF3E8] px-3.5 py-1 text-xs font-medium text-[#4C5E43]">
            <i className="fa-solid fa-comments text-[11px] text-[#556B48]" aria-hidden="true" />
            <span>Kontakt & tidsbestilling</span>
          </div>

          <h1 className="font-serif text-3.5xl sm:text-4xl lg:text-5xl font-medium text-[#222E1D] tracking-tight">
            Kontakt Trine Rosendal
          </h1>

          <p className="text-base sm:text-lg text-[#4D5E46] leading-relaxed">
            Du er altid velkommen til at kontakte mig med eventuelle spørgsmål. Ved manglende svar – læg en besked, og jeg ringer tilbage så hurtigt jeg kan.
          </p>
        </div>

        {/* Contact Info Cards & Online Booking CTA */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {/* Card 1: Ring */}
          <div className="rounded-2xl border border-[#D6E2CB] bg-[#F7F9F3] p-6 space-y-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E2EBD9] text-[#5C7A4F]">
              <i className="fa-solid fa-phone text-base" aria-hidden="true" />
            </div>
            <h3 className="font-serif text-lg font-medium text-[#222E1D]">
              Ring til mig
            </h3>
            <p className="text-xs text-[#5C6E52] leading-relaxed">
              Ring for spørgsmål eller en uforpligtende snak om et behandlingsforløb.
            </p>
            <a
              href="tel:42510105"
              className="inline-block pt-1 font-semibold text-[#5C7A4F] hover:text-[#222E1D] underline whitespace-nowrap"
            >
              42 51 01 05
            </a>
          </div>

          {/* Card 2: Send en email */}
          <div className="rounded-2xl border border-[#D6E2CB] bg-[#F7F9F3] p-6 space-y-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E2EBD9] text-[#5C7A4F]">
              <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
            </div>
            <h3 className="font-serif text-lg font-medium text-[#222E1D]">
              Send en email
            </h3>
            <p className="text-xs text-[#5C6E52] leading-relaxed">
              Skriv direkte til min indbakke. Jeg svarer så hurtigt som muligt.
            </p>
            <a
              href="mailto:info@trine-rosendal.dk"
              className="inline-block pt-1 font-semibold text-[#5C7A4F] hover:text-[#222E1D] underline whitespace-nowrap"
            >
              info@trine-rosendal.dk
            </a>
          </div>

          {/* Card 3: Online Booking */}
          <div className="rounded-2xl border-2 border-[#ECC572] bg-gradient-to-br from-[#FFFDF7] to-[#FEF8E8] p-6 space-y-3 flex flex-col justify-between shadow-xs">
            <div>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FEEEC7] text-[#935D08] mb-3">
                <i className="fa-solid fa-calendar-check text-base" aria-hidden="true" />
              </div>
              <h3 className="font-serif text-lg font-medium text-[#222E1D]">
                Online tidsbestilling
              </h3>
              <p className="text-xs text-[#6B5A42] leading-relaxed mt-1">
                Vælg selv din tid direkte i kalenderen i ro og mag.
              </p>
            </div>
            <a
              href="https://system.easypractice.net/book/online-booking-16"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F5D154] hover:bg-[#EABF35] px-4 py-2.5 text-xs font-semibold text-[#1E2718] shadow-xs transition"
            >
              <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
              <span>Book tid online nu</span>
            </a>
          </div>
        </div>

        {/* Form and Address Details */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Contact Form */}
          <div className="lg:col-span-7 rounded-3xl border border-[#D6E2CB] bg-[#F7F9F3] p-7 sm:p-10 shadow-xs">
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-[#222E1D] mb-2">
              Send en besked
            </h2>
            <p className="text-xs sm:text-sm text-[#5C6E52] mb-6">
              Udfyld formularen nedenfor, så vender jeg tilbage til dig hurtigst muligt.
            </p>

            {submitted ? (
              <div className="rounded-2xl border border-[#9FC4B0] bg-[#F2F8F4] p-8 text-center space-y-3">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#D7ECE0] text-[#2E7A4A]">
                  <i className="fa-solid fa-check text-lg" aria-hidden="true" />
                </div>
                <h3 className="font-serif text-xl font-medium text-[#1E5733]">
                  Tak for din henvendelse
                </h3>
                <p className="text-xs sm:text-sm text-[#386146] max-w-md mx-auto leading-relaxed">
                  Jeg har modtaget din besked og vender tilbage til dig lige så snart jeg kan.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-2 text-xs font-medium text-[#2E7A4A] underline"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" noValidate>
                {/* Antispam honeypot field (hidden with sr-only + absolute) */}
                <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="contact_website_url">Website</label>
                  <input
                    type="text"
                    id="contact_website_url"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={(e) =>
                      setFormData({ ...formData, website_url: e.target.value })
                    }
                  />
                </div>

                {serverError && (
                  <div className="rounded-xl border border-[#E5B5B5] bg-[#FDF2F2] p-4 text-xs text-[#8A2525]">
                    {serverError}
                  </div>
                )}

                {/* Navn */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-xs font-semibold text-[#222E1D] mb-1.5"
                  >
                    Navn <span className="text-[#963737]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value })
                      if (errors.name) setErrors({ ...errors, name: null })
                    }}
                    placeholder="Dit fulde navn"
                    className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#222E1D] placeholder:text-[#8D9F85] focus:outline-none focus:ring-1 ${
                      errors.name
                        ? 'border-[#B84D4D] focus:ring-[#B84D4D]'
                        : 'border-[#D6E2CB] focus:border-[#556B48] focus:ring-[#556B48]'
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-[#B84D4D]">{errors.name}</p>
                  )}
                </div>

                {/* Telefonnummer & Email (2 cols on sm) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-xs font-semibold text-[#222E1D] mb-1.5"
                    >
                      Telefon <span className="text-[#963737]">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({ ...formData, phone: e.target.value })
                        if (errors.phone) setErrors({ ...errors, phone: null })
                      }}
                      placeholder="F.eks. 42 51 01 05"
                      className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#222E1D] placeholder:text-[#8D9F85] focus:outline-none focus:ring-1 ${
                        errors.phone
                          ? 'border-[#B84D4D] focus:ring-[#B84D4D]'
                          : 'border-[#D6E2CB] focus:border-[#556B48] focus:ring-[#556B48]'
                      }`}
                    />
                    {errors.phone && (
                      <p className="mt-1 text-xs text-[#B84D4D]">{errors.phone}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-semibold text-[#222E1D] mb-1.5"
                    >
                      Email <span className="text-[#963737]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value })
                        if (errors.email) setErrors({ ...errors, email: null })
                      }}
                      placeholder="din@email.dk"
                      className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#222E1D] placeholder:text-[#8D9F85] focus:outline-none focus:ring-1 ${
                        errors.email
                          ? 'border-[#B84D4D] focus:ring-[#B84D4D]'
                          : 'border-[#D6E2CB] focus:border-[#556B48] focus:ring-[#556B48]'
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-[#B84D4D]">{errors.email}</p>
                    )}
                  </div>
                </div>

                {/* Beskrivelse / Besked */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs font-semibold text-[#222E1D] mb-1.5"
                  >
                    Beskrivelse <span className="text-[#963737]">*</span>
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    value={formData.message}
                    onChange={(e) => {
                      setFormData({ ...formData, message: e.target.value })
                      if (errors.message) setErrors({ ...errors, message: null })
                    }}
                    placeholder="Hvad ønsker du hjælp til eller høre mere om?"
                    className={`w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#222E1D] placeholder:text-[#8D9F85] focus:outline-none focus:ring-1 ${
                      errors.message
                        ? 'border-[#B84D4D] focus:ring-[#B84D4D]'
                        : 'border-[#D6E2CB] focus:border-[#556B48] focus:ring-[#556B48]'
                    }`}
                  />
                  {errors.message && (
                    <p className="mt-1 text-xs text-[#B84D4D]">{errors.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#5C7A4F] px-8 py-3.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#455C3A] disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <i className="fa-solid fa-circle-notch animate-spin text-xs" aria-hidden="true" />
                      <span>Sender besked...</span>
                    </>
                  ) : (
                    <>
                      <span>Send besked</span>
                      <i className="fa-solid fa-paper-plane text-xs text-[#E9D9E5]" aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Right Column: Clinic Portrait & Social */}
          <div className="lg:col-span-5 space-y-6">
            <div className="overflow-hidden rounded-3xl border border-[#DFE6D5] bg-white shadow-xs h-[292px] sm:h-[324px]">
              <img
                src="/cdn-cgi/image/width=600,quality=80,format=auto/_assets/cat-1011-scaled.jpg"
                alt="Trine Rosendal klinik"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src =
                    'https://assets.auxodev.dk/assets/0c569b3d-b522-4d44-8ece-350bff7b17df/cat-1011-scaled.jpg'
                }}
              />
            </div>

            <div className="rounded-2xl border border-[#D6E2CB] bg-[#F7F9F3] p-6 space-y-3">
              <div className="flex items-center gap-2.5 text-sm font-semibold text-[#222E1D]">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E2EBD9] text-[#5C7A4F]">
                  <i className="fa-solid fa-location-dot" aria-hidden="true" />
                </div>
                <span>Klinikkens adresse</span>
              </div>
              <p className="text-sm font-medium text-[#222E1D]">
                Prins Burisvej 41.A, 7500 Holstebro
              </p>
              <p className="text-xs text-[#5C6E52] leading-relaxed">
                Der er gode parkeringsmuligheder lige ved klinikken.
              </p>
            </div>

            <div className="rounded-2xl border border-[#DFE6D5] bg-[#F6F8F3] p-6 space-y-4">
              <h3 className="font-serif text-lg font-medium text-[#2B3824]">
                Følg klinikken på Facebook
              </h3>
              <p className="text-xs text-[#634E5E] leading-relaxed">
                Her deler jeg løbende gode råd, historier og nyt fra klinikken i Holstebro.
              </p>
              <a
                href="https://www.facebook.com/hypnosetrine/#"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20 px-4 py-2.5 text-xs font-semibold transition"
              >
                <i className="fa-brands fa-facebook text-base" aria-hidden="true" />
                <span>facebook.com/hypnosetrine</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
